import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  Stack,
  TextField,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import {
  Public as PublicIcon,
  FilterAlt as FilterAltIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { api_nocors } from '../../services/api';
import KanbanTagsNewAutoComplete from '../../components/KanbanTagsNewAutoComplete';
import PublicColumn from './PublicColumn';
import PublicCardModal from './PublicCardModal';

const DISPLAY_DEFAULTS = {
  showTaskCount: true,
  showTags: true,
  showPriorityName: true,
  showCardAuthorship: true,
};

const StyledSearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.text.primary, 0.02),
  transition: theme.transitions.create(['flex-basis', 'border-color', 'background-color'], { duration: 200 }),
  flexShrink: 1,
  flexBasis: 240,
  minWidth: 160,
  height: 36,
  '&:focus-within': {
    flexBasis: 360,
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  '& .MuiInputBase-input': {
    padding: '6px 8px 6px 4px',
    fontSize: '0.8rem',
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
}));

const LockedChipBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 10,
  border: `1px solid ${theme.palette.mode === 'dark' ? '#414955' : '#d8dce0'}`,
  backgroundColor: alpha(theme.palette.text.primary, 0.03),
  minHeight: 36,
}));

function PublicRequesterAutoComplete({ linkId, value, onChange, placeholder }) {
  const [searchParam, setSearchParam] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const handleSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setContacts([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoadingContacts(true);
    try {
      const { data } = await api_nocors.get(
        `/public/k/${linkId}/requesters`,
        {
          params: { searchParam: term.toLowerCase() },
          signal: abortRef.current.signal,
        }
      );
      setContacts(data?.contacts || []);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        console.error(err);
      }
    } finally {
      setLoadingContacts(false);
    }
  }, [linkId]);

  const handleInputChange = (_e, newValue) => {
    setSearchParam(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(newValue), 400);
  };

  const handleClear = () => {
    onChange(null);
    setSearchParam('');
    setContacts([]);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const getSecondaryText = (option) => {
    if (option?.isGroup) return 'Grupo';
    const parts = [];
    if (option?.number) parts.push(`+${option.number}`);
    if (option?.email) parts.push(option.email);
    if (option?.contract) parts.push(`#${option.contract}`);
    return parts.join(' | ') || '';
  };

  const noOptionsLabel = useMemo(() => {
    if (loadingContacts) return 'Buscando...';
    if (searchParam.length > 0 && searchParam.length < 2) return 'Digite ao menos 2 caracteres...';
    if (searchParam.length >= 2 && contacts.length === 0) return 'Nenhum solicitante encontrado';
    return 'Digite para pesquisar por nome, telefone, email ou contrato...';
  }, [loadingContacts, searchParam, contacts.length]);

  return (
    <Autocomplete
      size="small"
      sx={{ width: '100%' }}
      options={contacts}
      autoHighlight
      value={value || null}
      inputValue={searchParam}
      onInputChange={handleInputChange}
      onChange={(_e, newValue) => onChange(newValue || null)}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      getOptionLabel={(o) => (o?.name ? String(o.name) : '')}
      filterOptions={(x) => x}
      noOptionsText={noOptionsLabel}
      loading={loadingContacts}
      renderOption={(props, option) => (
        <Box
          component="li"
          key={option.id}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}
          {...props}
        >
          <Avatar src={option.profilePicUrl} sx={{ width: 32, height: 32, flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {getSecondaryText(option)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{ '& .MuiOutlinedInput-root': { padding: '4px 5px 4px 6px !important' } }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Typography
                  sx={{
                    marginLeft: '10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {placeholder}
                </Typography>
                <Divider sx={{ margin: '0px 10px' }} orientation="vertical" flexItem />
              </>
            ),
            endAdornment: (
              <>
                {loadingContacts ? (
                  <CircularProgress size={18} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {value && (
                      <IconButton onClick={handleClear} sx={{ padding: 0 }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                    <AddIcon />
                  </Box>
                )}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function KanbanShared() {
  const { linkId } = useParams();

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCardId, setOpenCardId] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tagsSelected, setTagsSelected] = useState([]);
  const [requesterSelected, setRequesterSelected] = useState(null);

  const initializedRef = useRef(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (searchTerm.trim()) params.q = searchTerm.trim();
    if (tagsSelected.length) params.tagIds = tagsSelected.map((t) => t.id).join(',');
    if (requesterSelected?.id) params.requesterId = String(requesterSelected.id);
    return params;
  }, [searchTerm, tagsSelected, requesterSelected]);

  useEffect(() => {
    if (!linkId) {
      setLoading(false);
      setError('Link inválido.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    api_nocors
      .get(`/public/k/${linkId}`, { params: buildParams() })
      .then((res) => {
        if (cancelled) return;
        setBoard(res.data);

        if (!initializedRef.current) {
          const presetTags = res.data?.presetFilters?.tags || [];
          const presetRequester = res.data?.presetFilters?.requester || null;
          if (presetTags.length >= 1) {
            setTagsSelected(presetTags);
          }
          if (presetRequester) {
            setRequesterSelected(presetRequester);
          }
          initializedRef.current = true;
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403) setError('Este quadro não está disponível publicamente.');
        else if (status === 404) setError('Link inválido ou expirado.');
        else setError('Não foi possível carregar o quadro.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, buildParams]);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const presetTags = board?.presetFilters?.tags || [];
  const presetRequester = board?.presetFilters?.requester || null;
  const availableTags = board?.availableFilters?.tags || [];

  const tagsLocked = presetTags.length === 1;

  const tagOptions = useMemo(() => {
    if (presetTags.length >= 2) return presetTags;
    return availableTags;
  }, [presetTags, availableTags]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleLoadMore = useCallback(
    async (columnId) => {
      if (!board || !linkId) return;
      const col = (board.column || []).find((c) => c.id === columnId);
      if (!col || !col.pagination?.hasMore) return;

      const nextPage = (col.pagination.page || 1) + 1;
      const limit = col.pagination.pageSize || 15;

      const { data } = await api_nocors.get(
        `/public/k/${linkId}/column/${columnId}/cards`,
        { params: { ...buildParams(), pageNumber: nextPage, limit } }
      );

      setBoard((prev) => {
        if (!prev) return prev;
        const target = (prev.column || []).find((c) => c.id === columnId);
        if (!target) return prev;
        const existingIds = new Set(target.cards.map((c) => c.id));
        const merged = [
          ...target.cards,
          ...(data.cards || []).filter((c) => !existingIds.has(c.id)),
        ];
        return {
          ...prev,
          column: prev.column.map((c) =>
            c.id === columnId
              ? { ...c, cards: merged, pagination: data.pagination }
              : c
          ),
        };
      });
    },
    [board, linkId, buildParams]
  );

  if (loading && !board) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: (t) => (t.palette.mode === 'dark' ? '#161c24' : '#ffffff'),
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: (t) => (t.palette.mode === 'dark' ? '#161c24' : '#ffffff'),
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verifique se o link está correto ou solicite um novo ao responsável pelo quadro.
        </Typography>
      </Box>
    );
  }

  const applied = board?.appliedFilters || {};
  const hasApplied = !!(applied.tags && applied.tags.length);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: (t) => (t.palette.mode === 'dark' ? '#161c24' : '#ffffff'),
      }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
          bgcolor: (t) => t.palette.background.paper,
          minHeight: 60,
        }}
      >
        <PublicIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {board?.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visualização pública • somente leitura
          </Typography>
        </Box>
        {hasApplied && (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <FilterAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Filtrado por:
            </Typography>
            {(applied.tags || []).map((t) => (
              <Chip
                key={t.id}
                size="small"
                label={t.title}
                sx={{
                  height: 22,
                  fontSize: '0.72rem',
                  bgcolor: t.color ? alpha(t.color, 0.18) : undefined,
                  color: t.color || undefined,
                  border: t.color ? `1px solid ${alpha(t.color, 0.5)}` : undefined,
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          py: 1,
          px: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}`,
          bgcolor: (t) => t.palette.background.paper,
        }}
      >
        <StyledSearchBar>
          <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 1, flexShrink: 0 }} />
          <StyledInputBase
            placeholder="Buscar por título, detalhes ou protocolo..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            endAdornment={
              searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
        </StyledSearchBar>

        <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
          {tagsLocked ? (
            <LockedChipBox>
              <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Marcador
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              {presetTags.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={t.title}
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    bgcolor: t.color ? alpha(t.color, 0.2) : undefined,
                    color: t.color || undefined,
                    border: t.color ? `1px solid ${alpha(t.color, 0.5)}` : undefined,
                  }}
                />
              ))}
            </LockedChipBox>
          ) : (
            <KanbanTagsNewAutoComplete
              labels={tagOptions}
              placeholder="Marcadores"
              search="Filtrar por marcadores"
              value={tagsSelected}
              setValue={setTagsSelected}
              loading={false}
              groupLabel="Marcadores do quadro"
              emptyLabel="Nenhum marcador selecionado"
            />
          )}
        </Box>

        <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
          <LockedChipBox>
            <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Solicitante
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {presetRequester ? (
              <>
                <Avatar
                  src={presetRequester?.profilePicUrl}
                  sx={{ width: 22, height: 22, fontSize: '0.7rem' }}
                >
                  {(presetRequester?.name || '?').slice(0, 1).toUpperCase()}
                </Avatar>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {presetRequester?.name}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Todos
              </Typography>
            )}
          </LockedChipBox>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '8px',
        }}
      >
        {(board?.column || []).map((col) => (
          <PublicColumn
            key={col.id}
            column={col}
            displayFilters={DISPLAY_DEFAULTS}
            onOpenCard={(cardId) => setOpenCardId(cardId)}
            onLoadMore={handleLoadMore}
          />
        ))}
      </Box>

      <PublicCardModal
        open={!!openCardId}
        onClose={() => setOpenCardId(null)}
        linkId={linkId}
        cardId={openCardId}
      />
    </Box>
  );
}
